<template>
  <v-container fluid class="list-page transaction-register">
    <div class="list-page-heading">
      <div><div class="list-page-heading__eyebrow">Billing</div><h1>Transactions</h1><p>Find processor charges and refunds without opening Stripe or PayCompass.</p></div>
    </div>
    <v-card outlined class="list-card">
      <div class="list-toolbar">
        <v-text-field v-model="search" outlined dense clearable hide-details prepend-inner-icon="mdi-magnify"
          label="Search transactions" placeholder="Last 4, reference, invoice, parent, donor, or amount" class="list-search" />
        <v-select v-model="selectedStatuses" :items="statusOptions" item-text="text" item-value="value"
          multiple clearable outlined dense hide-details small-chips deletable-chips
          prepend-inner-icon="mdi-filter-variant" label="Status" class="transaction-status-filter" />
        <div class="transaction-date-filter">
          <DateTimePicker defaultRange="since365DaysAgo" :range="true" :hideTime="true"
            v-model="dateTime" @submit="applyDateFilter" />
        </div>
      </div>
      <v-alert v-if="errorMessage" type="error" text class="mx-4 mt-4 mb-0">
        {{ errorMessage }}
      </v-alert>
      <v-data-table :headers="headers" :items="transactions" :loading="loading" :options.sync="options"
        :server-items-length="total" :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }" class="list-table">
        <template v-slot:item.createdAt="{ item }"><div class="font-weight-medium">{{ item.createdAt | moment("MMM D, YYYY") }}</div><div class="caption grey--text">{{ item.createdAt | moment("h:mm A") }}</div></template>
        <template v-slot:item.provider="{ item }"><PaymentProviderChip :provider="provider(item)" /></template>
        <template v-slot:item.card="{ item }"><div class="font-weight-medium text-capitalize">{{ item.cardBrand || "Card" }}</div><div class="caption grey--text">{{ item.last4 ? `•••• ${item.last4}` : "Last four unavailable" }}</div></template>
        <template v-slot:item.referenceNumber="{ item }">
          <button type="button" class="transaction-link transaction-reference" @click="openTransaction(item)">
            {{ item.referenceNumber || item.transactionId || `#${item.id}` }}
          </button>
        </template>
        <template v-slot:item.invoice="{ item }">
          <button v-if="item.Invoice" type="button" class="transaction-link font-weight-medium"
            :aria-label="`View invoice ${item.Invoice.localId}`" @click="openInvoice(item.Invoice)">
            #{{ item.Invoice.localId }}
          </button>
          <span v-else class="grey--text">Not linked</span>
        </template>
        <template v-slot:item.payer="{ item }">
          <button v-if="payerProfilePath(item)" type="button" class="transaction-link text-left"
            :aria-label="`Open ${payerType(item)} profile for ${payerName(item)}`" @click="openPayer(item)">
            <span class="d-block">{{ payerName(item) }}</span>
            <span class="caption text--secondary">{{ payerType(item) }}</span>
          </button>
          <span v-else class="grey--text">—</span>
        </template>
        <template v-slot:item.transactionType="{ item }"><v-chip x-small outlined :color="isRefund(item) ? 'deep-orange' : 'primary'">{{ isRefund(item) ? "Refund" : "Charge" }}</v-chip></template>
        <template v-slot:item.status="{ item }"><v-chip x-small :color="statusColor(item.status)" text-color="white">{{ item.status || "Unknown" }}</v-chip></template>
        <template v-slot:item.amount="{ item }"><strong :class="{ 'deep-orange--text': isRefund(item) }">{{ isRefund(item) ? "− " : "" }}{{ item.amount | currency }}</strong></template>
        <template v-slot:no-data><div class="py-10 text-center grey--text">No processor transactions found.</div></template>
      </v-data-table>
    </v-card>
    <TransactionDetail :openTransactionDetail="transactionDetailOpen" :selectedTransaction="selectedTransaction"
      @close="closeTransaction" />
  </v-container>
</template>

<script>
import TransactionService from "../service";
import PaymentProviderChip from "@/components/PaymentProviderChip.vue";
import DateTimePicker from "@/components/DateTimePicker.vue";
import TransactionDetail from "@/modules/Invoice/components/TransactionDetail.vue";
import Hashids from "hashids";

const hashids = new Hashids();

export default {
  name: "TransactionList",
  components: { PaymentProviderChip, DateTimePicker, TransactionDetail },
  data() {
    return {
      transactions: [], total: 0, loading: false, search: "", searchTimer: null, errorMessage: "", requestNumber: 0,
      transactionDetailOpen: false, selectedTransaction: null,
      selectedStatuses: [],
      statusOptions: [
        { text: "Approved", value: "Approved" },
        { text: "Settled", value: "settled" },
        { text: "Declined", value: "declined" },
        { text: "Error", value: "error" },
        { text: "Returned", value: "returned" },
        { text: "Voided", value: "voided" },
        { text: "Submitted", value: "Submitted" },
        { text: "Originated", value: "originated" },
        { text: "Captured", value: "captured" },
        { text: "Reserve", value: "reserve" },
      ],
      dateTime: {
        start: this.$moment().subtract(365, "days").startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      options: { page: 1, itemsPerPage: 25, sortBy: [], sortDesc: [] },
      headers: [
        { text: "Date", value: "createdAt", sortable: false }, { text: "Processor", value: "provider", sortable: false },
        { text: "Card", value: "card", sortable: false }, { text: "Reference", value: "referenceNumber", sortable: false },
        { text: "Invoice", value: "invoice", sortable: false }, { text: "Parent / donor", value: "payer", sortable: false },
        { text: "Activity", value: "transactionType", sortable: false }, { text: "Status", value: "status", sortable: false },
        { text: "Amount", value: "amount", align: "end", sortable: false },
      ],
    };
  },
  watch: {
    options: { deep: true, handler() { this.getList(); } },
    search() { clearTimeout(this.searchTimer); this.searchTimer = setTimeout(() => { this.options.page = 1; this.getList(); }, 350); },
    selectedStatuses() { this.options.page = 1; this.getList(); },
  },
  created() {
    document.title = "Taleem - Transactions";
    this.getList();
  },
  beforeDestroy() { clearTimeout(this.searchTimer); },
  methods: {
    async getList() {
      const requestNumber = ++this.requestNumber;
      this.loading = true; this.errorMessage = "";
      try {
        const response = await TransactionService.getAll({ includeInvoice: true, search: this.search || undefined,
          statuses: this.selectedStatuses.length ? this.selectedStatuses.join(",") : undefined,
          start: this.dateTime && this.dateTime.start, end: this.dateTime && this.dateTime.end,
          limit: this.options.itemsPerPage, offset: (this.options.page - 1) * this.options.itemsPerPage });
        if (requestNumber !== this.requestNumber) return;
        console.log("Transaction list response:", response);
        this.transactions = response.data || []; this.total = response.count || 0;
      } catch (error) {
        if (requestNumber !== this.requestNumber) return;
        this.transactions = []; this.total = 0;
        this.errorMessage = (error && error.data && error.data.message) || "Transactions could not be loaded. Confirm the backend was updated and restarted after the migration.";
      } finally { if (requestNumber === this.requestNumber) this.loading = false; }
    },
    applyDateFilter() { this.options.page = 1; this.getList(); },
    provider(item) { return item.provider || (item.Invoice && item.Invoice.paymentProvider) || "PAYCOMPASS"; },
    payerName(item) { const invoice = item.Invoice || {}; if (invoice.Customer) return invoice.Customer.fatherName || invoice.Customer.motherName || `Parent #${invoice.Customer.id}`; if (invoice.Donor) return invoice.Donor.name || `Donor #${invoice.Donor.id}`; return "—"; },
    payerType(item) { return item.Invoice && item.Invoice.DonorId ? "Donor" : "Parent"; },
    payerProfilePath(item) {
      const invoice = item.Invoice;
      if (!invoice) return null;
      if (invoice.DonorId) return `/donors/${invoice.DonorId}/profile`;
      if (invoice.CustomerId) return `/parents/${invoice.CustomerId}/profile`;
      return null;
    },
    openPayer(item) {
      const path = this.payerProfilePath(item);
      if (path) this.$router.push({ path });
    },
    openInvoice(invoice) {
      const hashId = hashids.encode(invoice.id);
      const path = invoice.DonorId
        ? `/global/invoice/${hashId}/donorInvoice`
        : `/global/invoice/${hashId}/view`;
      const route = this.$router.resolve({ path });
      window.open(route.href, "newwindow", "width=850, height=1100");
    },
    openTransaction(item) {
      this.selectedTransaction = item;
      this.transactionDetailOpen = true;
    },
    closeTransaction() {
      this.transactionDetailOpen = false;
      this.selectedTransaction = null;
    },
    isRefund(item) { return String(item.transactionType || "").toLowerCase() === "refund"; },
    statusColor(status) { const value = String(status || "").toLowerCase(); if (["approved", "succeeded", "paid", "settled"].some(word => value.includes(word))) return "primary"; if (["declined", "failed", "error"].some(word => value.includes(word))) return "red"; return "grey"; },
  },
};
</script>

<style scoped>
.transaction-register .list-search { max-width: 720px; }
.transaction-register .list-toolbar { align-items: center; display: flex; flex-wrap: wrap; gap: 16px; }
.transaction-register .transaction-status-filter { flex: 0 1 320px; max-width: 320px; }
.transaction-register .transaction-date-filter { margin-left: auto; min-width: 220px; }
.transaction-reference { font-family: monospace; overflow-wrap: anywhere; }
.transaction-link { padding: 0; border: 0; background: transparent; color: var(--v-primary-base); cursor: pointer; font: inherit; text-decoration: underline; text-underline-offset: 2px; }
.transaction-link:focus-visible { border-radius: 3px; outline: 2px solid var(--v-primary-base); outline-offset: 3px; }
@media (max-width: 700px) {
  .transaction-register .list-search,
  .transaction-register .transaction-status-filter,
  .transaction-register .transaction-date-filter { max-width: none; width: 100%; }
}
</style>
